import type { ILinkProps } from "@components/cms/link/Link";
import type { TextProps } from "@ui/components/content/text/Text";
import type { InputProps } from "@ui/components/forms/input/Input";
import type { BoxProps } from "@ui/components/layout/box/Box";
import type { ContainerProps } from "@ui/components/layout/container/Container";
import type { FlexProps } from "@ui/components/layout/flex/Flex";
import type { GridProps } from "@ui/components/layout/grid/Grid";
import type { NextLinkProps } from "@components/ui/next-link/NextLink";
import { scrollbarStyles } from "@ui/css/01.scrollbar/scrollbar.styles";
import type { MotionStyle } from "framer-motion";
import { sizes, space } from "ui/src/design-tokens";

export const logoContainerStyles: FlexProps["sx"] = {
    zIndex: 1,
    justifyContent: { base: "center", sm: "center", md: "center", lg: "flex-start" },
    px: 4,
    py: 2,
    bg: "white",
    height: sizes.navBarCollapsedHeight,
    alignItems: "center",
    boxShadow: "right",
};

export const mobileLogoContainerStyles: FlexProps["sx"] = {
    zIndex: 1,
    justifyContent: { base: "center", sm: "center", md: "center", xl: "flex-start" },
    paddingLeft: space["2"],
    paddingRight: space["2"],
    paddingTop: space["2"],
    paddingBottom: space["2"],
    bg: "white",
    w: "100%",
    height: sizes.navBarCollapsedHeight,
    backdropFilter: "blur(7.5px)",
    alignItems: "center",
    boxShadow: "right",
};

export const navContainerStyles: ContainerProps["sx"] = {
    justifyContent: "center",
    position: "sticky",
    top: 6,
    pl: ["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop", "layoutMargin.desktop"],
    pr: ["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop", "layoutMargin.desktop"],
    maxWidth: "100%",
    boxSizing: "border-box",
    mt: ["layoutMargin.mobile", "layoutMargin.tab", "layoutMargin.desktop", "layoutMargin.desktop"],
    zIndex: 11,
    w: "auto",
    bg: "transparent",
    border: ["none"],
};

// Sub menu list items
export const innerNavLeftGridStyles: GridProps["sx"] = {
    columnGap: 5,
    rowGap: 6,
    h: "fit-content",
    w: "100%",
    bg: "white",
};
export const outerNavLeftStyles: BoxProps["sx"] = {
    width: "100%",
    height: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 0,
    overflowX: "hidden",
    "&:hover": {
        ...scrollbarStyles,
        "::-webkit-scrollbar-track": {
            bg: "gray.200",
        },
    },
    "::-webkit-scrollbar": {
        width: sizes.customScrollbarWidth,
        height: sizes.customScrollbarWidth,
    },
    "::-webkit-scrollbar-thumb": { background: "transparent" },
    "::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-track": {
        background: "transparent",
    },
};

// Image to the right container
export const innerNavRightStyles: FlexProps["sx"] = {
    position: "sticky",
    maxHeight: sizes.full,
    width: "100%",
    overflow: "auto",
    flexDirection: "column",
    maxW: "100%",
    gap: 10,
    overflowX: "hidden",
    "&:hover": {
        "::-webkit-scrollbar-thumb": { background: "black" },
        "::-webkit-scrollbar-track": { background: "gray.200" },
    },
    "::-webkit-scrollbar": {
        width: sizes.customScrollbarWidth,
        height: sizes.customScrollbarWidth,
    },
    "::-webkit-scrollbar-thumb": { background: "transparent" },
    "::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-track": {
        background: "transparent",
    },
    paddingRight: "15px",
};
export const containerStyles: BoxProps["sx"] = {
    display: { base: "flex", sm: "flex", md: "flex", lg: "flex", xl: "flex" },
    color: "black",
    gap: space["0.2"],
    px: [0],
    alignItems: "center",
    bg: "transparent",
    justifyContent: { base: "flex-start" },
    gridTemplateColumns: "1fr 1fr 1fr",
    boxShadow: "navigation",
    w: ["auto", "auto", "auto", "auto", "fit-content"],
};

export const menuContainerStyles: ContainerProps["sx"] = {
    display: "flex",
    justifyContent: "space-between",
    gap: 3,
    height: `calc(${sizes.navExpandedHeight} - ${sizes.navBarCollapsedHeight})`,
    paddingLeft: "30px",
    paddingRight: "15px",
    paddingTop: "30px",
    paddingBottom: "30px",
};
export const rightContainerStyles: FlexProps["sx"] = {
    gap: 1,
    alignItems: "center",
};
export const navItemContainerStyles: FlexProps["sx"] = {
    px: 1,
    background: "linear-gradient(black 0 0) 12px 99% /var(--d, 0) 1.5px no-repeat",
    transition: "cubic-bezier(0.85, 0, 0.15, 1)0.5s",
    height: sizes.navBarCollapsedHeight,
    bg: "white",
    mr: -0.2,
};

// Sub-Menu Headings Titles
export const smallHeadingStyles: TextProps["sx"] = {
    fontWeight: "bold",
    color: "black",
    textTransform: "uppercase",
    fontSize: "desktopBodyTextNormal",
    marginBottom: 4,
};

export const navItemStyles: NextLinkProps["sx"] = {
    fontSize: "md",
    whiteSpace: "nowrap",
    fontWeight: "bold",
    px: 2,
};
export const linkItemStyles: ILinkProps["sx"] = {
    fontSize: "desktopBodyTextNormal",
    fontWeight: "semibold",
    whiteSpace: "wrap",
    lineHeight: "shorter",
    maxW: "225px",
};

export const titleLinkStyles: ILinkProps["sx"] = {
    ...linkItemStyles,
    textTransform: "uppercase",
    marginBottom: 4,
};

export const animatedContainerStyles: BoxProps["sx"] = {
    overflow: "hidden",
    background: "transparent",
    whiteSpace: "nowrap",
    position: "absolute",
    right: "0",
    left: ["initial", "initial", "initial", "-130px", "-130px"],
    top: "53px",
    height: "auto",
    maxH: "80vh",
    overscrollBehavior: "contain",
    w: ["100%", "100%", "calc(100vw - 60px)"],
};

export const animatedContainerMobileStyles: MotionStyle = {
    whiteSpace: "nowrap",
    position: "absolute",
    right: "0",
    left: "0",
    height: "auto",
    top: "53px",
    maxHeight: "calc(100dvh - 52px)",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
    zIndex: 44,
    gap: 32,
};

export const categoryTitleMobileStyles: TextProps["sx"] = {
    fontWeight: "semibold",
    color: "black",
    fontSize: "desktopHeading5",
};

export const mobileMenuContainerStyles: FlexProps["sx"] = {
    display: ["flex", "flex", "flex", "flex", "none"],
    pt: space["2"],
    flexDirection: "column",
    position: "relative",
    inset: "auto 0",
};

export const navItemMobileStyles: FlexProps["sx"] = {
    justifyContent: "space-between",
    alignItems: "center",
    w: "100%",
    alignContent: "center",
    bg: "transparent",
    pb: space["4"],
    px: 4,
    _active: { bg: "transparent" },
    _hover: { bg: "transparent" },
};

export const linksContainerStyles: FlexProps["sx"] = {
    justifyContent: "flex-start",
    backdropFilter: "blur(7.5px)",
    gap: space["0.2"],
    backgroundColor: "transparent",
    position: "relative",
    display: { base: "none", md: "none", lg: "flex" },
};

export const bottomContainerLinkStyles: NextLinkProps["sx"] = {
    display: "flex",
    fontSize: "desktopBodyTextNormal",
    gap: sizes[2],
    alignItems: "center",
};

export const bottomContainerMobileStyles: FlexProps["sx"] = {
    px: 4,
    flexDirection: "column",
    bg: "white",
    py: 4,
    gap: 4,
};

// Second Sub-Menu
export const mobileButtonBackStyles: FlexProps["sx"] = {
    w: "100%",
    bg: "white",
    justifyContent: "flex-start",
    alignContent: "center",
    fontWeight: "semibold",
    color: "black",
    gap: 2,
    fontSize: "desktopHeading5",
};

//Second submenu back button style adjust pb and pt
export const seeAllCategoriesMobileStyles: FlexProps["sx"] = {
    fontWeight: "semibold",
    color: "black",
    fontSize: "desktopHeading5",
    pb: space["3"],
    pt: 5,
    px: 4,
    textTransform: "uppercase",
    alignItems: "center",
    justifyContent: "space-between",
};

export const parentThumbnailContainerStyles: BoxProps["sx"] = {
    position: "relative",
    height: "auto",
    my: 6,
};

export const thumbnailCarouselContainerStyles: FlexProps["sx"] = {
    display: "flex",
    maxWidth: "100vw",
    flexWrap: "nowrap",
    overflowY: "auto",
    pb: 2,
    ...scrollbarStyles,
    left: 0,
    /* We are using negative value to avoid cut the scroll area */
    px: 4,
    "::-webkit-scrollbar-track": {
        background: "gray.200",
        marginRight: 4,
        marginLeft: 4,
    },
};

export const thumbnailCarouselItemStyles: FlexProps["sx"] = {
    height: "100%",
    gap: 2,
    flexWrap: "nowrap",
    width: "auto",
};

export const mobileLinkStyles: ILinkProps["sx"] = {
    ...categoryTitleMobileStyles,
    textTransform: "uppercase",
    py: space["3"],
    px: 4,
    _hover: {
        borderColor: "transparent",
        outline: "none",
    },
    _focus: {
        outlineColor: "transparent",
        outline: "none",
        border: "none",
    },
    _focusVisible: {
        outlineColor: "transparent",
        outline: "none",
        border: "none",
    },
    _active: {
        outlineColor: "transparent",
        outline: "none",
        border: "none",
    },
};

//Search input - reuse the input field for the mobile design
export const searchInputStyles: InputProps["sx"] = {
    color: "black",
    borderRadius: "none",
    transform: "scale(1)",
    marginRight: 0,
    px: 10,
    py: 3,
    height: [sizes.navBarCollapsedHeight],
    zIndex: 2,
    borderColor: {
        base: "gray.200",
        lg: "transparent",
    },
    fontSize: "desktopBodyTextSmall",
    _placeholder: {
        color: "black",
        fontSize: " desktopBodyTextSmall",
        whiteSpace: "nowrap",
        textOverflow: "elipsis",
    },
    _hover: {
        outline: "none",
    },
    _focus: {
        outlineColor: "initial",
        outline: "none",
    },
    _focusVisible: {
        outlineColor: "initial",
        outline: "none",
    },
    _active: {
        outlineColor: "initial",
        outline: "none",
    },
};

export const desktopNavWrapperStyles: FlexProps["sx"] = {
    gap: 0.2,
    display: { base: "none", xl: "flex" },
};

export const iconWrapperStyles: FlexProps["sx"] = {
    display: { base: "flex", xl: "none" },
    gap: "0.2",
    w: "fit-content",
};

export const toggleNavigationButtonStyles: FlexProps["sx"] = {
    mr: "0.2",
    backdropFilter: "blur(7.5px)",
    boxShadow: "right",
};

export const desktopNavItemWrapperStyles: FlexProps["sx"] = {
    boxShadow: "right",
};

export const salesNavItemContainerStyles: FlexProps["sx"] = {
    ...navItemContainerStyles,
    bg: "#A42B1E",
};
export const salesNavItemStyles: NextLinkProps["sx"] = {
    ...navItemStyles,
    color: "white !important",
};

export const salesNavItemMobileStyles: FlexProps["sx"] = {
    ...navItemMobileStyles,
    bg: "#A42B1E",
    _active: { bg: "#A42B1E" },
    _hover: { bg: "#A42B1E" },
};
export const salesCategoryTitleMobileStyles: TextProps["sx"] = {
    ...categoryTitleMobileStyles,
    color: "white",
};
export const salesMobileButtonBackStyles: FlexProps["sx"] = {
    ...mobileButtonBackStyles,
    bg: "#A42B1E",
    color: "white",
    _hover: {
        bg: "#B85347",
        color: "white",
    },
    _active: { color: "white" },
};
